import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle2, XCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, AiBanner, StatusBadge, Section } from "@/components/kit";
import { WorkoutEditor, MealEditor } from "@/components/PlanEditor";
import useLoad from "@/lib/useLoad";
import api, { errMsg } from "@/lib/api";

function AssignBox({ planId }) {
  const nav = useNavigate();
  const [clients] = useLoad("/clients");
  const [cid, setCid] = useState("");
  const assign = async () => {
    try { const { data } = await api.post(`/plans/${planId}/assign`, { client_id: cid }); toast.success(`Plan assigned to ${data.client_name}`); nav(`/coach/clients/${cid}?tab=plans`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title="Assign to client">
      <div className="flex gap-2">
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger data-testid="plan-assign-client-select"><SelectValue placeholder="Choose client" /></SelectTrigger>
          <SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={assign} disabled={!cid} data-testid="plan-assign-btn"><Send size={14} className="mr-1" />Assign</Button>
      </div>
      <p className="text-xs text-slate-500 mt-2">A copy is assigned; editing this template later won't change the client's plan. The client's previous active plan of this type is archived.</p>
    </Section>
  );
}

export default function PlanPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const goBack = () => window.history.state?.idx > 0 ? nav(-1) : nav("/coach/templates");
  const [plan, reload] = useLoad(`/plans/${id}`);
  const [draft, setDraft] = useState(null);
  const [savedContent, setSavedContent] = useState(null);
  useEffect(() => { if (plan && !Array.isArray(plan)) { setDraft(plan); setSavedContent(plan.content); } }, [plan]);
  if (!draft) return <div className="eyebrow">Loading plan…</div>;

  const isDraft = draft.status === "ai_draft" || draft.status === "draft";
  const save = async (quiet) => {
    const content = draft.type === "workout"
      ? {
        ...draft.content,
        days: Array.from({ length: 7 }, (_, index) => ({
          ...(draft.content?.days?.[index] || { exercises: [] }),
          name: `Day ${index + 1}`,
        })),
      }
      : draft.content;
    const { data } = await api.put(`/plans/${id}`, { title: draft.title, description: draft.description, content });
    setDraft((current) => ({ ...current, content: data.content }));
    setSavedContent(data.content);
    if (!quiet) toast.success("Plan saved");
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  const approve = () => act(async () => { await save(true); await api.post(`/plans/${id}/approve`); reload(); },
    draft.client_id ? "Plan approved & assigned to client" : "Approved and saved to library");
  const reject = () => act(async () => { await api.post(`/plans/${id}/reject`); nav("/coach/library"); }, "Draft rejected");
  const Editor = draft.type === "workout" ? WorkoutEditor : MealEditor;

  return (
    <div>
      <PageHeader eyebrow={`${draft.type} plan · ${draft.client_name ? `for ${draft.client_name}` : "library template"}`} title={draft.type === "workout" ? "Workout builder" : "Meal builder"} testid="plan-page-title" leading={
        <Button type="button" variant="ghost" size="sm" onClick={goBack} aria-label="Go to previous page" title="Go to previous page" data-testid="plan-back-btn"><ArrowLeft size={16} className="mr-1" />Back</Button>
      }>
        <StatusBadge status={draft.status} testid="plan-status-badge" />
        <Button variant="outline" onClick={() => act(() => save(true), "Plan saved")} data-testid="plan-save-btn"><Save size={14} className="mr-1" />Save changes</Button>
      </PageHeader>
      {draft.status === "ai_draft" && <AiBanner flags={draft.ai_meta?.flags} model={draft.ai_meta?.model} />}
      <div className="grid xl:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="h-12 text-xl font-display uppercase font-semibold bg-[#111827]" data-testid="plan-title-input" />
          <Editor content={draft.content || {}} savedContent={savedContent} onChange={(content) => setDraft({ ...draft, content })} />
        </div>
        <div className="space-y-4 xl:sticky xl:top-6 self-start">
          {isDraft && (
            <Section title="Actions">
              <div className="flex flex-col gap-2">
                <Button onClick={approve} className="bg-emerald-600 hover:bg-emerald-500" data-testid="ai-draft-approve-button"><CheckCircle2 size={14} className="mr-1" />{draft.client_id ? "Approve & assign" : "Approve to library"}</Button>
                {draft.status === "ai_draft" && <Button variant="ghost" onClick={reject} data-testid="ai-draft-reject-button"><XCircle size={14} className="mr-1" />Reject draft</Button>}
              </div>
            </Section>
          )}
          {draft.status === "template" && <AssignBox planId={id} />}
          {draft.ai_meta?.inputs && (
            <Section title="AI inputs">
              <div className="space-y-1 text-xs text-slate-400">{Object.entries(draft.ai_meta.inputs).filter(([, v]) => v).map(([k, v]) => <div key={k}><span className="text-slate-500">{k}:</span> {String(v)}</div>)}</div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
